import http, { abortAllSharedHttpInflight } from '@utils/http';
import { resetAppQueryClientCaches } from '@app/appQueryClient';
import { create } from 'zustand';
import { ENV } from '@configs/env';
import { persist } from 'zustand/middleware';
import { refreshTokensSingleton } from '@services/refresh.service';
import { extractRolesFromUser } from '@ability/constants';
import { normalizePermissions, setPermissionsInLocalStorage } from '@utils/authPermissions';

export { normalizePermissions } from '@utils/authPermissions';

/**
 * Normalize user from login/refresh: ensure permissions are string[] and role is set from role or roles[0].
 */
const normalizeAuthUser = (user) => {
    if (!user) return null;
    const permissions = normalizePermissions(user.permissions);
    const role = user.role ?? (Array.isArray(user.roles) && user.roles.length ? user.roles[0] : null);
    return { ...user, permissions, role };
};

function clearDataFetchingOnUnauthenticated() {
    abortAllSharedHttpInflight();
    resetAppQueryClientCaches();
}

export const useAuthStore = create(
    persist(
        (set, get) => ({
            user: null,
            role: null,
            roles: [],
            permissions: [],
            planKey: null,
            isAuthenticated: false,
            accessToken: null,
            refreshToken: null,
            isRefreshing: false,

            setPlanKey: (planKey) => set({ planKey }),

            /**
             * GET /api/v1/auth/me
             * Refresh the current user profile + permissions and mirror permissions into localStorage.
             * Keeps existing tokens (SSE/event-driven refresh shouldn't disturb current session).
             */
            refreshMe: async () => {
                try {
                    const res = await http.get('/auth/me');
                    return get().applyMeProfile(res?.data);
                } catch {
                    return null;
                }
            },

            /**
             * Apply user from GET/PATCH /auth/me (or nested { user }) into store + localStorage.
             */
            applyMeProfile: (responseData) => {
                const raw = responseData?.data?.data ?? responseData?.data ?? responseData;
                const rawUser = raw?.user ?? raw;
                const withAvatar =
                    rawUser && typeof rawUser === 'object'
                        ? {
                              ...rawUser,
                              avatarUrl:
                                  rawUser.avatarUrl ?? rawUser.avatar ?? rawUser.profileImage ?? null,
                          }
                        : rawUser;
                const user = normalizeAuthUser(withAvatar);
                if (!user) return null;

                const { roles } = extractRolesFromUser(user);
                const planKey =
                    user.planKey ?? (typeof user.plan === 'string' ? user.plan : user.plan?.key) ?? null;

                set((state) => ({
                    user,
                    role: user?.role ?? null,
                    roles: roles ?? [],
                    permissions: user?.permissions || [],
                    planKey,
                    isAuthenticated: state.isAuthenticated ?? true,
                }));
                setPermissionsInLocalStorage(user?.permissions || []);
                return user;
            },

            onboardTheClient: async (data) => {
                const currentUser = get().user;
                try {
                    const response = await http.patch('/user/onboard-the-client', data);
                    if (response?.data?.success) {
                        set({
                            user: {
                                ...currentUser,
                                isOnboarded: true,
                            },
                        });
                    }
                    return { message: response?.data?.data?.message || response?.data?.message };
                } catch (error) {
                    const errorMessage =
                        error?.response?.data?.error?.message ||
                        error?.response?.data?.message ||
                        error?.response?.data?.error ||
                        error?.message ||
                        'Onboard client failed';
                    throw new Error(errorMessage);
                }
            },

            updateUserStore: async (userData) => {
                const currentUser = get().user;
                const allowedFields = ['name', 'email', 'phone'];
                const updatedUserData = {};

                for (const field of allowedFields) {
                    const newValue = userData[field];
                    const currentValue = currentUser[field];

                    const isFieldEmpty = newValue === undefined || newValue === null || newValue === '';
                    const isNewButEmpty = currentValue === undefined && isFieldEmpty;
                    const isChanged = newValue && newValue !== currentValue;

                    // Only including if it's changed AND not a new-empty field
                    if (!isNewButEmpty && isChanged) {
                        updatedUserData[field] = newValue;
                    }
                }

                if (Object.keys(updatedUserData).length === 0) {
                    return { status: 304, message: 'No changes detected' };
                }

                try {
                    const response = await http.patch('/user/update/profile', updatedUserData);
                    set((state) => ({
                        user: {
                            ...state.user,
                            ...updatedUserData,
                        },
                    }));
                    return { message: response?.data?.data?.message || response?.data?.message };
                } catch (error) {
                    const errorMessage =
                        error?.response?.data?.error?.message ||
                        error?.response?.data?.message ||
                        error?.response?.data?.error ||
                        error?.message ||
                        'Update user failed';
                    throw new Error(errorMessage);
                }
            },

            setSessionFromVerifyEmail: (payload) => {
                const data = payload?.data ?? payload;
                if (!data?.accessToken || !data?.user) return;
                const user = normalizeAuthUser(data.user);
                const { roles } = extractRolesFromUser(user);
                set({
                    user,
                    role: user.role ?? null,
                    roles: roles ?? [],
                    permissions: user.permissions ?? [],
                    planKey: user.planKey ?? (typeof user.plan === 'string' ? user.plan : user.plan?.key) ?? null,
                    isAuthenticated: true,
                    accessToken: data.accessToken,
                    refreshToken: data.refreshToken,
                    isRefreshing: false,
                });
                setPermissionsInLocalStorage(user.permissions ?? []);
            },

            login: async (email, password) => {
                try {
                    const response = await http.post('/auth/login', { email, password });
                    const { data } = response.data;
                    const user = normalizeAuthUser(data.user);
                    const { roles } = extractRolesFromUser(user);
                    const planKey =
                        data.plan?.key ??
                        user?.planKey ??
                        (typeof user?.plan === 'string' ? user.plan : user?.plan?.key) ??
                        null;
                    set({
                        user,
                        role: user?.role ?? null,
                        roles: roles ?? [],
                        permissions: user?.permissions || [],
                        planKey,
                        isAuthenticated: true,
                        accessToken: data.accessToken,
                        refreshToken: data.refreshToken,
                        isRefreshing: false,
                    });
                    setPermissionsInLocalStorage(user?.permissions || []);
                    return response;
                } catch (error) {
                    const errorMessage =
                        error?.response?.data?.error?.message ||
                        error?.response?.data?.message ||
                        error?.response?.data?.error ||
                        error?.message ||
                        'Login failed';
                    throw new Error(errorMessage);
                }
            },

            register: async (formData) => {
                try {
                    const response = await http.post('/auth/register', formData);
                    const { data } = response.data;

                    if (response.status === 200 && response.data.message !== '') {
                        return {
                            routeToLogin: true,
                            message: response.data.message,
                        };
                    } else {
                    const user = normalizeAuthUser(data.user);
                    const { roles } = extractRolesFromUser(user);
                    const planKey =
                            user?.planKey ??
                            (typeof user?.plan === 'string' ? user.plan : user?.plan?.key) ??
                            null;
                    set({
                            user,
                            role: user?.role ?? null,
                            roles: roles ?? [],
                            permissions: user?.permissions || [],
                            planKey,
                            isAuthenticated: true,
                            accessToken: data.accessToken,
                            refreshToken: data.refreshToken,
                            isRefreshing: false,
                        });
                    setPermissionsInLocalStorage(user?.permissions || []);
                        return response;
                    }
                } catch (error) {
                    const errorMessage =
                        error?.response?.data?.error?.message ||
                        error?.response?.data?.message ||
                        error?.response?.data?.error ||
                        error?.message ||
                        'Registration failed';
                    throw new Error(errorMessage);
                }
            },

            logout: async () => {
                // Clear auth state and disconnect realtime SYNCHRONOUSLY first so any
                // navigation that follows (e.g. navigate('/sign-in') in the caller) lands on
                // a fully de-authed app. Otherwise Signin briefly sees isAuthenticated=true
                // and queues a redirect back to the dashboard, leaving the user stuck on a
                // blank "redirecting…" loader after the API call finally resolves.
                const refreshTokenValue = get().refreshToken;
                clearDataFetchingOnUnauthenticated();
                import('@services/realtime.socket').then((m) => m.disconnectRealtimeSocket()).catch(() => {});
                setPermissionsInLocalStorage([]);
                set({
                    user: null,
                    role: null,
                    roles: [],
                    permissions: [],
                    planKey: null,
                    isAuthenticated: false,
                    accessToken: null,
                    refreshToken: null,
                    isRefreshing: false,
                });

                // Fire-and-forget revoke on the server. Failures are non-fatal for the UI.
                if (refreshTokenValue) {
                    try {
                        await http.post(
                            '/auth/logout',
                            {},
                            {
                                headers: { 'x-refresh-token': refreshTokenValue },
                            },
                        );
                    } catch (error) {
                        const errorMessage =
                            error?.response?.data?.error?.message ||
                            error?.response?.data?.message ||
                            error?.response?.data?.error ||
                            error?.message ||
                            'Logout failed';
                        console.error('Logout failed:', errorMessage);
                    }
                }
            },

            hardLogout: () => {
                clearDataFetchingOnUnauthenticated();
                import('@services/realtime.socket').then((m) => m.disconnectRealtimeSocket()).catch(() => {});
                setPermissionsInLocalStorage([]);
                set({
                    user: null,
                    role: null,
                    roles: [],
                    permissions: [],
                    planKey: null,
                    isAuthenticated: false,
                    accessToken: null,
                    refreshToken: null,
                    isRefreshing: false,
                });
            },

            checkAuth: async () => {
                // Prevent concurrent calls
                if (get().isRefreshing) {
                    return { success: false, message: 'Already checking auth' };
                }

                // Check if we have tokens in storage
                const storedAccessToken = get().accessToken;
                const storedRefreshToken = get().refreshToken;

                // If no tokens, clear auth state
                if (!storedAccessToken && !storedRefreshToken) {
                    clearDataFetchingOnUnauthenticated();
                    setPermissionsInLocalStorage([]);
                    set({
                        user: null,
                        role: null,
                        roles: [],
                        permissions: [],
                        planKey: null,
                        isAuthenticated: false,
                        accessToken: null,
                        refreshToken: null,
                        isRefreshing: false,
                    });
                    return { success: false };
                }

                set({ isRefreshing: true });

                try {
                    const response = await http.get('/auth/check');
                    const { data } = response.data;

                    // Preserve existing tokens if not provided in response
                    const currentAccessToken = get().accessToken;
                    const currentRefreshToken = get().refreshToken;

                    const user = normalizeAuthUser(data.user);
                    const planKey =
                        user?.planKey ??
                        (typeof user?.plan === 'string' ? user.plan : user?.plan?.key) ??
                        null;
                    const { roles } = extractRolesFromUser(user);
                    set({
                        user,
                        role: user?.role ?? null,
                        roles: roles ?? [],
                        permissions: user?.permissions || [],
                        planKey,
                        isAuthenticated: true,
                        accessToken: data.accessToken || currentAccessToken,
                        refreshToken: data.refreshToken || currentRefreshToken,
                        isRefreshing: false,
                    });
                    setPermissionsInLocalStorage(user?.permissions || []);
                    return { success: true };
                } catch (error) {
                    // If check fails, try to refresh token
                    const refreshTokenValue = get().refreshToken;

                    if (!refreshTokenValue) {
                        clearDataFetchingOnUnauthenticated();
                        setPermissionsInLocalStorage([]);
                        set({
                            user: null,
                            role: null,
                            roles: [],
                            permissions: [],
                            planKey: null,
                            isAuthenticated: false,
                            accessToken: null,
                            refreshToken: null,
                            isRefreshing: false,
                        });
                        return { success: false };
                    }

                    try {
                        const refreshed = await refreshTokensSingleton(() => get().refreshToken, set, get().logout);

                        if (!refreshed || !refreshed.accessToken) {
                            clearDataFetchingOnUnauthenticated();
                            setPermissionsInLocalStorage([]);
                            set({
                                user: null,
                                role: null,
                                roles: [],
                                permissions: [],
                                planKey: null,
                                isAuthenticated: false,
                                accessToken: null,
                                refreshToken: null,
                                isRefreshing: false,
                            });
                            return { success: false };
                        }

                        // Update tokens after successful refresh
                        set({
                            accessToken: refreshed.accessToken,
                            refreshToken: refreshed.refreshToken,
                            isRefreshing: false,
                        });
                        return { success: true };
                    } catch (refreshError) {
                        console.error('Auth check and refresh both failed:', refreshError);
                        clearDataFetchingOnUnauthenticated();
                        setPermissionsInLocalStorage([]);
                        set({
                            user: null,
                            role: null,
                            roles: [],
                            permissions: [],
                            planKey: null,
                            isAuthenticated: false,
                            accessToken: null,
                            refreshToken: null,
                            isRefreshing: false,
                        });
                        return { success: false };
                    }
                }
            },
        }),
        {
            name: ENV.NODE_ENV === 'development' ? 'sprinto-auth-store-local' : 'sprinto-auth-store-live',
            getStorage: () => localStorage,
            partialize: (state) => ({
                user: state.user,
                role: state.role,
                roles: state.roles,
                permissions: state.permissions,
                planKey: state.planKey,
                isAuthenticated: state.isAuthenticated,
                accessToken: state.accessToken,
                refreshToken: state.refreshToken,
            }),
            onRehydrateStorage: () => (rehydratedState) => {
                useAuthStore.setState({ isRefreshing: false });
                const pk = rehydratedState?.planKey;
                if (pk != null && typeof pk === 'object' && (pk.key ?? pk.name)) {
                    useAuthStore.getState().setPlanKey(pk.key ?? pk.name);
                }
            },
        },
    ),
);
